import { EventItemAttributes } from '@/types'
import { UpdateEventItemMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditEventItemQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { useEffect, useState } from 'react'
import { Query as TableQuery } from './table_query'
import { Button } from '@/components/base/button'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { ErrorMessage, Field, Label } from '../base/fieldset'
import { Selector as ItemSelector } from './selector'
import { Selector as StageSelector } from '../event_stage/selector'
import { Inline as Error } from '@/components/error'

type Event = EditEventItemQuery['events']['nodes'][0]

interface Props {
  eventId: string
  id: string
  onSuccess: () => void
}

export const Form = ({ eventId, id, onSuccess }: Props) => {
  const {
    attributes,
    setAttributes,
    save,
    saveSuccess,
    formBuilder,
    queryError,
    fieldErrors,
    queryLoading,
    mutationLoading
  } = useForm<UpdateEventItemMutation, EventItemAttributes, EditEventItemQuery, Event>({
    queryVariables: { eventId, eventItemId: id },
    id,
    initializer: event => {
      const eventItem = event.eventItems.nodes[0]
      return {
        eventId,
        eventStageId: eventItem.eventStage?.id || '',
        itemId: eventItem.item.id,
        itemType: eventItem.item.__typename || '',
        notes: eventItem.notes || '',
        discount: eventItem.discount,
        quantity: eventItem.quantity,
        accessories: [],
      }
    },
    query: EditQuery,
    queryKey: 'events',
    mutation: UpdateMutation,
    resultKey: 'updateEventItem',
    refetchQueries: [TableQuery]
  })

  const [showDelete, setShowDelete] = useState(false)

  useEffect(() => { if (saveSuccess) onSuccess() }, [saveSuccess, onSuccess])

  const onDelete = () => {
    setShowDelete(false)
    onSuccess()
  }

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        <Field>
          <Label>Item</Label>
          <ItemSelector
            invalid={!!fieldErrors?.itemId}
            value={`${attributes.itemType}:${attributes.itemId}`}
            onChange={value => {
              const item = value?.id?.split(':') || []
              setAttributes({ ...attributes, itemId: item[1] || '', itemType: item[0] || '' })
            }}
          />
          { fieldErrors?.itemId && <ErrorMessage>{ fieldErrors?.itemId }</ErrorMessage> }
        </Field>
        <Field>
          <Label>Stage</Label>
          <StageSelector
            eventId={eventId}
            invalid={!!fieldErrors?.eventStageId}
            value={attributes.eventStageId}
            onChange={value => {
              setAttributes({ ...attributes, eventStageId: value?.id || '' })
            }}
          />
          { fieldErrors?.eventStageId && <ErrorMessage>{ fieldErrors?.eventStageId }</ErrorMessage> }
        </Field>
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildField({ name: 'Quantity', field: 'quantity', type: 'integer' }) }
          { formBuilder.buildField({ name: 'Discount', field: 'discount', type: 'percentage' }) }
        </div>
        { formBuilder.buildField({ name: 'Notes', field: 'notes', type: 'textarea' }) }
        <div className="flex flex-row justify-between">
          <Button color="red" onClick={() => setShowDelete(true) }>Delete</Button>
          { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
        </div>
        <DeleteDialog
          id={id}
          mutation={DeleteMutation}
          open={showDelete}
          refetchQueries={[TableQuery]}
          setOpen={setShowDelete}
          onSuccess={onDelete}
        />
      </div>
    </>
  )
}
